export type ColorMode = "dark" | "light";
export type ColorModePreference = ColorMode | "system";

export const colorModeKey = "ark-color-mode";
const colorModeEvent = "ark:color-mode-change";
const darkQuery = "(prefers-color-scheme: dark)";

type ColorModeWindow = Window & {
  __arkColorMode?: { preference: ColorModePreference; volatile: boolean };
};

export function isColorModePreference(
  value: unknown,
): value is ColorModePreference {
  return value === "dark" || value === "light" || value === "system";
}

export function resolveColorMode(
  preference: ColorModePreference,
  systemDark: boolean,
): ColorMode {
  return preference === "system" ? (systemDark ? "dark" : "light") : preference;
}

export function readColorModePreference(): ColorModePreference {
  if (typeof window === "undefined") return "system";
  const cache = (window as ColorModeWindow).__arkColorMode;
  if (cache?.volatile) return cache.preference;
  try {
    const value = window.localStorage.getItem(colorModeKey);
    return isColorModePreference(value) ? value : "system";
  } catch {
    return cache?.preference ?? "system";
  }
}

export function setColorModePreference(preference: ColorModePreference) {
  if (typeof window === "undefined" || !isColorModePreference(preference))
    return;
  let volatile = false;
  try {
    window.localStorage.setItem(colorModeKey, preference);
  } catch {
    volatile = true;
  }
  // Share unavailable-storage preferences across independently loaded apps.
  (window as ColorModeWindow).__arkColorMode = { preference, volatile };
  window.dispatchEvent(new Event(colorModeEvent));
}

export function subscribeColorModePreference(listener: () => void) {
  if (typeof window === "undefined") return () => undefined;
  const onStorage = (event: StorageEvent) => {
    if (event.key !== null && event.key !== colorModeKey) return;
    delete (window as ColorModeWindow).__arkColorMode;
    listener();
  };
  window.addEventListener(colorModeEvent, listener);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(colorModeEvent, listener);
    window.removeEventListener("storage", onStorage);
  };
}

export function readSystemDark() {
  return (
    typeof window !== "undefined" &&
    Boolean(window.matchMedia?.(darkQuery).matches)
  );
}

export function subscribeSystemMode(listener: () => void) {
  if (typeof window === "undefined") return () => undefined;
  const media = window.matchMedia?.(darkQuery);
  media?.addEventListener("change", listener);
  return () => media?.removeEventListener("change", listener);
}
